'use client';

import { PlusIcon, ShareIcon, Smartphone, X } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Row, Stack } from '@/components/layout';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';

interface InstallAlertProps {
    show: boolean;
}

const DISMISS_KEY = 'installAlertDismissed';

export const InstallAlert = ({ show }: InstallAlertProps) => {
    const [isDismissed, setIsDismissed] = useState(false);

    useEffect(() => {
        try {
            const stored = window.localStorage.getItem(DISMISS_KEY);
            if (stored === 'true') {
                // eslint-disable-next-line react-hooks/set-state-in-effect
                setIsDismissed(true);
            }
        } catch (error) {
            console.warn('Unable to read install alert state', error);
        }
    }, []);

    const handleDismiss = () => {
        try {
            window.localStorage.setItem(DISMISS_KEY, 'true');
        } catch (error) {
            console.warn('Unable to persist install alert state', error);
        }
        setIsDismissed(true);
    };

    if (!show) {
        return null;
    }

    if (isDismissed) {
        return null;
    }

    return (
        <Alert className="flex items-start gap-3 p-4">
            <Row className="grow items-start gap-3">
                <Smartphone className="mt-0.5 size-4 min-w-4" />
                <Stack className="grow gap-2">
                    <AlertTitle>App is available!</AlertTitle>

                    <AlertDescription>
                        To install this app on your iOS device, tap the share
                        button{' '}
                        <ShareIcon className="inline size-4 align-middle" /> and
                        then &quot;Add to Home Screen&quot;{' '}
                        <PlusIcon className="inline size-4 align-middle" />
                    </AlertDescription>
                </Stack>
            </Row>

            <Button
                size="icon-sm"
                variant="ghost"
                aria-label="Dismiss install alert"
                onClick={handleDismiss}
            >
                <X className="size-4" />
            </Button>
        </Alert>
    );
};
